import { useState } from 'react';
import GenericWizard from 'components/GenericWizard';
import BasicModalWithConfirmation from 'components/Modals/presets/BasicModalWithConfirmation';
import { useTranslation } from 'react-i18next';
import { useWindowSize } from 'react-use';
import { _modalSize } from 'components/Modals';
import { isValidPhoneNumber } from 'libphonenumber-js/max';
import { FIELD_TYPES } from 'utility/constants';
import { isEmailStatusField } from 'checks/fields';
import Loader from 'components/Kizen/Loader';
import { useEntityPredefinedOptions } from 'ts-components/hooks/activities/useEntityPredefinedOptions';
import { generateRedirtectURL } from 'components/Inputs/RedirectUrlInput';
import {
  CompleteActivityWizardContext,
  useCompleteActivityWizardContext,
} from 'components/GenericWizard/wizards/completeActivity/context';
import { toastVariant, useToast } from 'components/ToastProvider';

const flatValue = (value) => {
  if (Array.isArray(value)) {
    return value.map((v) => flatValue(v));
  }
  if (value && typeof value === 'object' && 'id' in value) return value.id;
  if (value && typeof value === 'object' && 'e164' in value) return value.e164;
  return value;
};
const isFieldShouldBePresent = (field, relatedObjects) => {
  return !(
    isEmailStatusField(field.fieldData) &&
    !field.value &&
    !relatedObjects.find(({ isContact }) => isContact)
  );
};

const getAssociationErrors = (context) => {
  const { requiredAssociations, activityRelatedObjects, formData } = context;

  const errors = activityRelatedObjects.map((obj) => {
    if (!requiredAssociations.includes(obj.id)) {
      return false;
    }

    return !formData.relatedObjects.some((ro) => ro.customObject === obj.id);
  });

  return errors;
};

const CompleteActivityModalInner = ({
  networkError,
  activityId,
  activityName,
  onConfirm,
  disabled,
  isLoading,
  ...modalProps
}) => {
  const ctx = useCompleteActivityWizardContext();

  const {
    activity,
    activityRelatedObjects,
    errors,
    formData,
    isActivityDisabled,
    isDirty,
    isLoading: contextLoading,
    isRuleActiveForLayoutItem,
    loadActivityError,
  } = ctx;

  const { t } = useTranslation();
  const { width } = useWindowSize();
  const [showToast] = useToast();
  const [associationErrors, setAssociationErrors] = useState(
    Array(activityRelatedObjects.length).fill(false)
  );
  const [showErrors, setShowErrors] = useState(
    Array(formData.fields.length).fill(false)
  );

  return (
    <BasicModalWithConfirmation
      show
      dirty={isDirty}
      typeOfContent={width >= _modalSize.medium ? 'wizard' : 'wizard-mobile'}
      buttonText={t('Complete')}
      disabled={
        disabled || contextLoading || loadActivityError || isActivityDisabled
      }
      leftBtn={null}
      hasOneBtnSubmit
      heading={
        activityName
          ? t('Completing {{activityName}} Activity', { activityName })
          : t('Log Activity')
      }
      size="medium"
      {...modalProps}
      onConfirm={async () => {
        const associationErrors = getAssociationErrors(ctx);

        if (associationErrors.some(Boolean) || errors.some(Boolean)) {
          setAssociationErrors(
            associationErrors.map((error) => {
              return error
                ? { message: t('This association is required.') }
                : error;
            })
          );

          setShowErrors(
            errors.map((error) => {
              return error ? { message: t('This field is required.') } : error;
            })
          );

          return;
        }

        const { redirectUrl, submissionAction } = activity;
        const result = await onConfirm({
          activityObjectId: activityId,
          payload: {
            ...formData,
            fields: formData.fields
              .filter((field) => {
                return (
                  isFieldShouldBePresent(field, formData.relatedObjects) &&
                  isRuleActiveForLayoutItem(field.id)
                );
              })
              .map(({ id, displayName, fieldType, value }) => {
                if (
                  value &&
                  typeof value === 'string' && // we do not convert old object phone numbers
                  fieldType === FIELD_TYPES.PhoneNumber.type &&
                  !isValidPhoneNumber(value)
                ) {
                  value = null;
                }
                return {
                  id,
                  displayName,
                  fieldType,
                  value: flatValue(value),
                };
              }),
          },
          dirtyFields: isDirty,
        });

        if (result?.success) {
          if (submissionAction === 'redirect' && redirectUrl) {
            const url = generateRedirtectURL({
              redirectUrl: activity.redirectUrl,
              passVariablesOnRedirect: activity.passVariablesOnRedirect,
              redirectParameterFields: activity.redirectParameterFields,
              fields: formData.fields,
              notes: formData.notes,
            });

            if (result?.mutationResult?.id) {
              url.searchParams.set(
                'logged_activity_id',
                result.mutationResult.id
              );
            }

            window.open(url.toString(), '_blank');
          }
        } else {
          const message = result?.errors?.non_field_errors?.[0];

          if (message) {
            showToast({ message, variant: toastVariant.FAILURE });
          }
        }
      }}
      enforceFocus={false}
    >
      {isLoading ? (
        <Loader loading />
      ) : (
        <GenericWizard
          wizard="CompleteActivitytWizard"
          associationErrors={associationErrors}
          showErrors={showErrors}
          setAssociationErrors={setAssociationErrors}
          setShowErrors={setShowErrors}
        />
      )}
    </BasicModalWithConfirmation>
  );
};

export const CompleteActivityModal = (props) => {
  const {
    predefinedOptions: existingPredefinedOptions,
    activity,
    activityId: id,
    customObjectId,
    disabled,
    isClient,
    entityId,
    entityLabel,
    networkError,
    onConfirm,
    onHide,
    show,
  } = props;

  const activityId = id ?? activity.activityObject?.id;

  const areDefaultAssignmentsFromScheduledActivity = Boolean(
    existingPredefinedOptions
  );

  const { predefinedOptions, loading } = useEntityPredefinedOptions({
    entityId,
    entityLabel,
    isClient,
    customObjectId,
    activityId,
    enabled: !areDefaultAssignmentsFromScheduledActivity,
  });

  if (!show) {
    return null;
  }

  return (
    <CompleteActivityWizardContext
      data={activity}
      predefinedOptions={existingPredefinedOptions || predefinedOptions}
      isLoadingPredefinedOptions={loading}
      areDefaultAssignmentsFromScheduledActivity={
        areDefaultAssignmentsFromScheduledActivity
      }
    >
      <CompleteActivityModalInner
        activityId={activityId}
        activityName={activity.activityObject?.name}
        disabled={disabled}
        networkError={networkError}
        onConfirm={onConfirm}
        onHide={onHide}
      />
    </CompleteActivityWizardContext>
  );
};
